/**
 * RadioGroup - Group of radio options
 *
 * Controlled usage:
 * <RadioGroup
 *   label="Plan"
 *   value={plan}
 *   onChange={setPlan}
 *   options={[
 *     { value: 'free', label: 'Free' },
 *     { value: 'pro', label: 'Pro', description: '$10/month' },
 *   ]}
 * />
 *
 * Use with React Hook Form:
 * <RadioGroup
 *   name="plan"
 *   label="Plan"
 *   options={[
 *     { value: 'free', label: 'Free' },
 *     { value: 'pro', label: 'Pro', description: '$10/month' },
 *   ]}
 *   error={errors.plan?.message}
 *   {...register('plan')}
 * />
 */

import { forwardRef } from 'react';

import { cn } from '../utils';

export interface RadioOption {
  /** Value for this option */
  value: string;
  /** Display label */
  label: string;
  /** Optional description */
  description?: string;
  /** Disable this option */
  disabled?: boolean;
}

export interface RadioGroupProps {
  /** Name attribute - optional for controlled component, required for uncontrolled/form */
  name?: string;
  /** Group label */
  label: string;
  /** Hide the label visually (still accessible to screen readers) */
  hideLabel?: boolean;
  /** Available options */
  options: RadioOption[];
  /** Currently selected value (controlled) */
  value?: string;
  /** Callback when value changes (controlled) */
  onChange?: (value: string) => void;
  /** Error message from form validation */
  error?: string;
  /** Optional helper text */
  hint?: string;
  /** Layout direction - 'direction' is legacy alias */
  orientation?: 'vertical' | 'horizontal';
  direction?: 'vertical' | 'horizontal';
  /** Size variant */
  size?: 'sm' | 'md';
  /** Additional class names */
  className?: string;
}

export const RadioGroup = forwardRef<HTMLInputElement, RadioGroupProps>(
  (
    {
      name,
      label,
      hideLabel,
      options,
      error,
      hint,
      orientation,
      direction = 'vertical',
      size = 'md',
      className,
      value: selectedValue,
      onChange,
    },
    ref
  ) => {
    const effectiveOrientation = orientation ?? direction;

    const handleChange = (optionValue: string) => {
      onChange?.(optionValue);
    };

    return (
      <fieldset className={className}>
        <legend
          className={cn(
            'block text-sm font-medium text-gray-700 dark:text-zinc-300',
            hideLabel && 'sr-only'
          )}
        >
          {label}
        </legend>
        <div
          className={cn(
            hideLabel ? '' : 'mt-2',
            effectiveOrientation === 'horizontal' ? 'flex flex-wrap gap-4' : 'space-y-2',
            size === 'sm' && effectiveOrientation === 'horizontal' && 'gap-2'
          )}
        >
          {options.map((option, index) => (
            <label
              key={option.value}
              className={cn(
                'flex cursor-pointer',
                option.description ? 'items-start' : 'items-center',
                option.disabled && 'cursor-not-allowed opacity-50',
                size === 'sm' ? 'gap-1.5' : 'gap-3',
                size === 'sm' &&
                  effectiveOrientation === 'horizontal' &&
                  'rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 transition-colors hover:border-gray-300 dark:border-white/[0.06] dark:bg-zinc-800 dark:hover:border-white/[0.1]',
                size === 'sm' &&
                  effectiveOrientation === 'horizontal' &&
                  selectedValue === option.value &&
                  'border-blue-500 bg-blue-50 dark:border-blue-500 dark:bg-blue-500/10'
              )}
            >
              <div
                className={cn(
                  'relative flex-shrink-0',
                  option.description && 'mt-0.5',
                  size === 'sm' && effectiveOrientation === 'horizontal' && 'hidden'
                )}
              >
                <input
                  ref={index === 0 ? ref : undefined}
                  type="radio"
                  name={name}
                  className="peer sr-only"
                  disabled={option.disabled}
                  value={option.value}
                  checked={selectedValue === option.value}
                  onChange={() => handleChange(option.value)}
                />
                <div
                  className={cn(
                    'rounded-full border transition-all',
                    'bg-gray-50 dark:bg-[#18181b]',
                    'peer-focus-visible:ring-2 peer-focus-visible:ring-blue-500 peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-white dark:peer-focus-visible:ring-offset-[#09090b]',
                    'peer-checked:border-blue-500',
                    error ? 'border-red-500' : 'border-gray-300 dark:border-white/[0.06]',
                    size === 'sm' ? 'h-4 w-4' : 'h-5 w-5'
                  )}
                />
                <div
                  className={cn(
                    'pointer-events-none absolute rounded-full bg-blue-500 opacity-0 transition-opacity peer-checked:opacity-100',
                    size === 'sm' ? 'left-1 top-1 h-2 w-2' : 'left-1.5 top-1.5 h-2 w-2'
                  )}
                />
              </div>
              {/* Hidden radio for keyboard accessibility in sm horizontal mode */}
              {size === 'sm' && effectiveOrientation === 'horizontal' && (
                <input
                  ref={index === 0 ? ref : undefined}
                  type="radio"
                  name={name}
                  className="sr-only"
                  disabled={option.disabled}
                  value={option.value}
                  checked={selectedValue === option.value}
                  onChange={() => handleChange(option.value)}
                />
              )}
              <div className="flex flex-col">
                <span
                  className={cn(
                    'font-medium',
                    size === 'sm' ? 'text-xs' : 'text-sm',
                    selectedValue === option.value && size === 'sm'
                      ? 'text-blue-700 dark:text-blue-400'
                      : 'text-gray-700 dark:text-zinc-300'
                  )}
                >
                  {option.label}
                </span>
                {option.description && (
                  <span className="text-sm text-gray-500 dark:text-zinc-500">
                    {option.description}
                  </span>
                )}
              </div>
            </label>
          ))}
        </div>
        {error && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>}
        {hint && !error && <p className="mt-2 text-sm text-gray-500 dark:text-zinc-500">{hint}</p>}
      </fieldset>
    );
  }
);

RadioGroup.displayName = 'RadioGroup';
